import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { ActionDetail, AreaListItem, ContactPickItem, ListResult } from '@tww/shared';
import { fmtDate, fmtEur, treePrices, type PriceBasis } from '@tww/shared';
import { api, ApiRequestError } from '../../api/client';
import { useLookup } from '../../api/hooks';
import { ContactPicker } from '../../ui/ContactPicker';
import { ErrorState, Note, Skeleton } from '../../ui/basics';
import { ComboField, Checkbox, DateInput, NumberInput, Select, TextArea, TextInput } from '../../ui/fields';
import { useToast } from '../../ui/Toast';

type F = Record<string, unknown>;
const pick = (c: { id: number; display_name: string; rating_grade?: any } | null): ContactPickItem | null => (c ? { id: c.id, display_name: c.display_name, sub: null, rating_grade: c.rating_grade ?? null, rating_reason: null, city: null, region: null, last_price: null, categories: [] } : null);
const STR = ['code', 'type_code', 'season_code', 'date', 'date_end', 'time_start', 'time_end', 'state_code', 'region', 'city', 'team_initials', 'species_text', 'donation_received_at', 'agreements_note', 'planting_comment', 'preparations_note', 'rental_car', 'impact_report_sent_at', 'donation_receipt_sent_at', 'nest_boxes', 'notes'] as const;
const NUM = ['year', 'trees_planned', 'trees_calculated', 'trees_by_participants', 'hectares', 'participants_planned', 'children_planned', 'participants_actual', 'children_actual', 'donation_pledged', 'donation_received_prev_year', 'donation_received_this_year', 'culture_costs_expected', 'price_per_tree', 'vat_rate'] as const;
/** Fehler dieser Schlüssel stehen direkt am Feld */
const FIELD_KEYS = new Set<string>([...STR, ...NUM, 'partner_contact_id', 'forester_contact_id', 'owner_contact_id', 'area_id']);

/** Stammdaten bearbeiten (Plan 04 Abschnitt 5.5): eigene Seite mit Abschnitten und Zusammenfassung rechts */
export function ActionFormPage() {
  const { id } = useParams();
  const aid = Number(id);
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['action', aid], queryFn: () => api.get<ActionDetail>(`/actions/${aid}`) });
  const [f, setF] = useState<F | null>(null);
  const [partner, setPartner] = useState<ContactPickItem | null>(null);
  const [forester, setForester] = useState<ContactPickItem | null>(null);
  const [owner, setOwner] = useState<ContactPickItem | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [regen, setRegen] = useState(false);
  const types = useLookup('action_types');
  const states = useLookup('states');
  const regions = useLookup('regions');
  const vatRates = useLookup('vat_rates');
  const areas = useQuery({ queryKey: ['areas-all'], queryFn: () => api.get<ListResult<AreaListItem>>('/areas?pageSize=500&sort=name'), staleTime: 60_000 });
  useEffect(() => {
    if (!q.data || f) return;
    const a = q.data;
    const o: F = {};
    for (const k of STR) o[k] = (a as any)[k] ?? '';
    for (const k of NUM) o[k] = (a as any)[k] ?? null;
    o.press_review = a.press_review;
    o.price_basis = a.price_basis;
    o.area_id = a.area?.id ?? null;
    setF(o);
    setPartner(pick(a.partner));
    setForester(pick(a.forester));
    setOwner(pick(a.owner));
  }, [q.data, f]);
  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { updated_at: q.data!.updated_at, regenerate_code: regen };
      for (const k of STR) body[k] = k === 'code' ? (String(f![k] ?? '').trim() || undefined) : String(f![k] ?? '').trim() || null;
      for (const k of NUM) body[k] = f![k] ?? null;
      body.press_review = Boolean(f!.press_review);
      body.price_basis = f!.price_basis ?? 'brutto';
      body.area_id = f!.area_id ?? null;
      body.partner_contact_id = partner?.id ?? q.data!.partner?.id;
      body.forester_contact_id = forester?.id ?? null;
      body.owner_contact_id = owner?.id ?? null;
      return api.patch<ActionDetail & { dues_reset?: { changed: number; kept: number } }>(`/actions/${aid}`, body);
    },
    onSuccess: (a) => {
      qc.invalidateQueries({ queryKey: ['action', aid] });
      qc.invalidateQueries({ queryKey: ['actions'] });
      qc.invalidateQueries({ queryKey: ['today'] });
      toast(a.dues_reset ? `Gespeichert · ${a.dues_reset.changed} Fristen neu berechnet${a.dues_reset.kept ? `, ${a.dues_reset.kept} eigene Fristen nicht angepasst` : ''}` : 'Gespeichert');
      nav(`/aktionen/${aid}?tab=stammdaten`);
    },
    onError: (e: Error) => {
      if (e instanceof ApiRequestError && e.fields) setErrors(e.fields);
      toast(e.message, 'error');
    },
  });
  if (q.isLoading || !f) return <Skeleton rows={8} />;
  if (q.isError) return <ErrorState error={q.error} retry={() => q.refetch()} />;
  const a = q.data!;
  const s = (k: string) => (f[k] as string) ?? '';
  const n = (k: string) => (f[k] as number | null) ?? null;
  const set = (k: string, v: unknown) => setF((x) => ({ ...x!, [k]: v }));
  const dateChanged = s('date') !== (a.date ?? '');
  return (
    <>
      <div className="crumb">
        <Link to="/aktionen">Aktionen</Link> › <Link to={`/aktionen/${aid}`}>{a.label}</Link>
      </div>
      <div className="head">
        <div>
          <h1>Stammdaten bearbeiten</h1>
          <div className="sub">{a.code}</div>
        </div>
      </div>
      {Object.keys(errors).length > 0 && (
        <div className="form-errors">
          Bitte die markierten Felder prüfen.
          {Object.entries(errors).filter(([k]) => !FIELD_KEYS.has(k)).length > 0 && (
            <ul>
              {Object.entries(errors)
                .filter(([k]) => !FIELD_KEYS.has(k))
                .map(([k, v]) => (
                  <li key={k}>{v}</li>
                ))}
            </ul>
          )}
        </div>
      )}
      <div className="page-form">
        <div className="col">
          <Section title="Kennung und Termin">
            <div className="form">
              <TextInput label="Kennung" value={s('code')} onChange={(v) => set('code', v)} error={errors.code} hint={a.code !== s('code') ? 'Die alte Kennung bleibt als Alias suchbar' : undefined} />
              <Select label="Aktionstyp" error={errors.type_code} value={s('type_code')} onChange={(v) => set('type_code', v)} options={types.values.map((t) => ({ value: t.code, label: t.label }))} placeholder="wählen" />
              <DateInput label="Aktionstag" error={errors.date} value={s('date')} onChange={(v) => set('date', v)} hint={dateChanged ? 'Standardfristen werden neu berechnet; eigene Fristen bleiben' : undefined} />
              <DateInput label="Ende (mehrtägig)" error={errors.date_end} value={s('date_end')} onChange={(v) => set('date_end', v)} />
              <TextInput label="Uhrzeit von" error={errors.time_start} value={s('time_start')} onChange={(v) => set('time_start', v)} type="time" />
              <TextInput label="Uhrzeit bis" error={errors.time_end} value={s('time_end')} onChange={(v) => set('time_end', v)} type="time" />
              <Select label="Saison" error={errors.season_code} value={s('season_code')} onChange={(v) => set('season_code', v)} options={[{ value: 'F', label: 'Frühjahr' }, { value: 'S', label: 'Sommer' }, { value: 'H', label: 'Herbst' }]} placeholder="aus Datum" />
              <NumberInput label="Jahr" error={errors.year} value={n('year')} onChange={(v) => set('year', v)} />
              <TextInput label="Team (Kürzel)" error={errors.team_initials} value={s('team_initials')} onChange={(v) => set('team_initials', v)} />
              {dateChanged && <Checkbox value={regen} onChange={setRegen} label="Kennung aus dem neuen Datum neu bilden" hint="Empfohlen, wenn noch keine Rechnung mit der alten Kennung existiert" />}
            </div>
          </Section>
          <Section title="Partner und Ort">
            <div className="form">
              <ContactPicker label="Spendenpartner" required value={partner} onChange={setPartner} category="partner" error={errors.partner_contact_id} />
              <ContactPicker label="Förster" error={errors.forester_contact_id} value={forester} onChange={setForester} category="forester" region={s('region') || undefined} hint="wird in den Punkt „Zuständiger Förster“ übernommen" />
              <ContactPicker label="Waldbesitzer" error={errors.owner_contact_id} value={owner} onChange={setOwner} category="owner" region={s('region') || undefined} />
              <Select label="Bundesland" error={errors.state_code} value={s('state_code')} onChange={(v) => set('state_code', v)} options={states.values.map((x) => ({ value: x.code, label: x.label }))} />
              <ComboField label="Region" error={errors.region} value={s('region')} onChange={(v) => set('region', v)} options={regions.values.filter((r) => !s('state_code') || !(r.meta as { state?: string } | null)?.state || (r.meta as { state?: string }).state === s('state_code')).map((r) => ({ value: r.code, label: r.label }))} newHint="wird als neue Region gespeichert" />
              <TextInput label="Ort" error={errors.city} value={s('city')} onChange={(v) => set('city', v)} />
            </div>
          </Section>
          <Section title="Fläche und Bäume">
            <div className="form">
              <Select label="Fläche" error={errors.area_id} value={f.area_id ? String(f.area_id) : ''} onChange={(v) => set('area_id', v ? Number(v) : null)} options={(areas.data?.items ?? []).map((x) => ({ value: String(x.id), label: `${x.name}${x.region ? ` (${x.region})` : ''}` }))} placeholder="keine Fläche" hint="Neue Flächen entstehen unter Flächen oder mit der Begehung" />
              <NumberInput label="Hektar" error={errors.hectares} value={n('hectares')} onChange={(v) => set('hectares', v)} decimals={2} />
              <NumberInput label="Bäume geplant" error={errors.trees_planned} value={n('trees_planned')} onChange={(v) => set('trees_planned', v)} />
              <NumberInput label="Bäume berechnet" error={errors.trees_calculated} value={n('trees_calculated')} onChange={(v) => set('trees_calculated', v)} />
              <NumberInput label="durch Teilnehmende" error={errors.trees_by_participants} value={n('trees_by_participants')} onChange={(v) => set('trees_by_participants', v)} />
              <TextInput label="Baumarten" error={errors.species_text} value={s('species_text')} onChange={(v) => set('species_text', v)} full hint="z. B. Traubeneiche 1.200, Esskastanie 400" />
            </div>
          </Section>
          <Section title="Teilnehmende">
            <div className="form">
              <NumberInput label="Teilnehmende geplant" error={errors.participants_planned} value={n('participants_planned')} onChange={(v) => set('participants_planned', v)} />
              <NumberInput label="davon Kinder" error={errors.children_planned} value={n('children_planned')} onChange={(v) => set('children_planned', v)} />
              <NumberInput label="Teilnehmende real" error={errors.participants_actual} value={n('participants_actual')} onChange={(v) => set('participants_actual', v)} />
              <NumberInput label="davon Kinder real" error={errors.children_actual} value={n('children_actual')} onChange={(v) => set('children_actual', v)} />
            </div>
          </Section>
          <Section title="Finanzen">
            <div className="form">
              <NumberInput label="Spendenzusage (€)" error={errors.donation_pledged} value={n('donation_pledged')} onChange={(v) => set('donation_pledged', v)} decimals={2} />
              <NumberInput label={`Baumpreis ${s('price_basis') === 'netto' ? 'netto' : 'brutto'} (€/Baum)`} error={errors.price_per_tree} value={n('price_per_tree')} onChange={(v) => set('price_per_tree', v)} decimals={2} hint={priceHint(n('price_per_tree'), s('price_basis') as PriceBasis, n('vat_rate'))} />
              <Select label="Baumpreis vereinbart als" value={s('price_basis') || 'brutto'} onChange={(v) => set('price_basis', v || 'brutto')} options={[{ value: 'brutto', label: 'brutto' }, { value: 'netto', label: 'netto' }]} />
              <Select label="Steuersatz" error={errors.vat_rate} value={n('vat_rate') == null ? '' : String(n('vat_rate'))} onChange={(v) => set('vat_rate', v === '' ? null : Number(v))} options={vatRates.values.map((x) => ({ value: String(Number(x.code)), label: x.label }))} placeholder="offen" />
              <NumberInput label="Spenden im Vorjahr erhalten (€)" error={errors.donation_received_prev_year} value={n('donation_received_prev_year')} onChange={(v) => set('donation_received_prev_year', v)} decimals={2} />
              <NumberInput label="Spenden aktuelles Jahr erhalten (€)" error={errors.donation_received_this_year} value={n('donation_received_this_year')} onChange={(v) => set('donation_received_this_year', v)} decimals={2} />
              <DateInput label="Eingang am" error={errors.donation_received_at} value={s('donation_received_at')} onChange={(v) => set('donation_received_at', v)} />
              <NumberInput label="Kulturkosten erwartet (€)" error={errors.culture_costs_expected} value={n('culture_costs_expected')} onChange={(v) => set('culture_costs_expected', v)} decimals={2} />
              <TextArea label="Erläuterung Vereinbarungen" error={errors.agreements_note} value={s('agreements_note')} onChange={(v) => set('agreements_note', v)} />
            </div>
          </Section>
          <Section title="Nachbereitung">
            <div className="form">
              <DateInput label="Wirkungsbericht raus am" error={errors.impact_report_sent_at} value={s('impact_report_sent_at')} onChange={(v) => set('impact_report_sent_at', v)} />
              <DateInput label="Spendenbescheinigung raus am" error={errors.donation_receipt_sent_at} value={s('donation_receipt_sent_at')} onChange={(v) => set('donation_receipt_sent_at', v)} />
              <TextInput label="Nistkästen" error={errors.nest_boxes} value={s('nest_boxes')} onChange={(v) => set('nest_boxes', v)} />
              <TextInput label="Mietwagen" error={errors.rental_car} value={s('rental_car')} onChange={(v) => set('rental_car', v)} />
              <Checkbox value={Boolean(f.press_review)} onChange={(v) => set('press_review', v)} label="Pressespiegel vorhanden" />
              <TextArea label="Kommentar Pflanzung" error={errors.planting_comment} value={s('planting_comment')} onChange={(v) => set('planting_comment', v)} />
              <TextArea label="Vorbereitungen" error={errors.preparations_note} value={s('preparations_note')} onChange={(v) => set('preparations_note', v)} />
              <TextArea label="Notizen" error={errors.notes} value={s('notes')} onChange={(v) => set('notes', v)} />
            </div>
          </Section>
        </div>
        <div className="aside">
          <div className="section">
            <div className="sb summary-box">
              <strong>{a.label}</strong>
              <dl>
                <dt>Aktionstag</dt>
                <dd>{s('date') ? fmtDate(s('date')) : 'offen'}</dd>
                <dt>Förster</dt>
                <dd>{forester?.display_name ?? 'offen'}</dd>
                <dt>Fläche</dt>
                <dd>{areas.data?.items.find((x) => x.id === f.area_id)?.name ?? 'keine'}</dd>
              </dl>
              {a.own_due_count > 0 && dateChanged && <Note kind="info">{a.own_due_count} eigene Fristen werden nicht angepasst.</Note>}
            </div>
          </div>
          <div className="actions-col">
            <button type="button" className="btn primary" disabled={save.isPending || !partner} onClick={() => save.mutate()}>
              Speichern
            </button>
            <Link className="btn" to={`/aktionen/${aid}`}>
              Abbrechen
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
/** Gegenseite des Baumpreises als Hinweis: „netto 4,67 €, brutto 5,00 €“ */
function priceHint(price: number | null, basis: PriceBasis, vat: number | null): string | undefined {
  if (price == null) return undefined;
  if (vat == null) return 'Steuersatz wählen, dann wird netto und brutto gerechnet';
  const p = treePrices(price, basis, vat);
  return `netto ${fmtEur(p.net)}, brutto ${fmtEur(p.gross)}`;
}
function Section({ title, desc, children }: { title: string; desc?: string; children: React.ReactNode }) {
  return (
    <div className="section">
      <div className="sh">
        <h2>{title}</h2>
        {desc && <span className="desc">{desc}</span>}
      </div>
      <div className="sb">{children}</div>
    </div>
  );
}
